import {defineStore} from 'pinia'
import type {LanguageOption} from '~/types/language';
import {TimeZones} from "~/utils/misc";


export const LanguageOption_Global: LanguageOption = {code: 'en', region: '', flag: '🌍', label: 'Global', currency: 'USD'};
export const LanguageOption_DE: LanguageOption = {code: 'de', region: 'DE', flag: '🇩🇪', label: 'Deutsch', currency: 'EUR'};
export const LanguageOption_UK: LanguageOption = {code: 'en', region: 'UK', flag: '🇬🇧', label: 'English', currency: 'GBP'};
export const LanguageOption_HK: LanguageOption = {code: 'en', region: 'HK', flag: '🇭🇰', label: 'English', currency: 'HKD'};
export const LanguageOption_US: LanguageOption = {code: 'en', region: 'US', flag: '🇺🇸', label: 'English', currency: 'USD'};
export const LanguageOption_ES: LanguageOption = {code: 'es', region: 'ES', flag: '🇪🇸', label: 'Español', currency: 'EUR'};
export const LanguageOption_FR: LanguageOption = {code: 'fr', region: 'FR', flag: '🇫🇷', label: 'Français', currency: 'EUR'};
export const LanguageOption_IT: LanguageOption = {code: 'it', region: 'IT', flag: '🇮🇹', label: 'Italiana', currency: 'EUR'};
export const LanguageOption_RO: LanguageOption = {code: 'ro', region: 'RO', flag: '🇷🇴', label: 'Română', currency: 'RON'};

export const AvailableLanguages: LanguageOption[] = [
    LanguageOption_Global,
    LanguageOption_DE,
    LanguageOption_UK,
    LanguageOption_HK,
    LanguageOption_US,
    LanguageOption_ES,
    LanguageOption_FR,
    LanguageOption_IT,
    LanguageOption_RO
]


export const useLanguageStore = defineStore('language', () => {
    const { $i18n } = useNuxtApp();
    const localStoreName = 'itlm-v1-lang';

    // Initialize with the server-side default. This will be used for the initial render.
    const selected = ref<LanguageOption>(LanguageOption_Global);
    const isLoaded = ref(false);

    watch(selected, (newSelected) => {
        useHead({
            htmlAttrs: {
                lang: newSelected.code
            }
        })
    }, { immediate: true })

    onMounted(() => {
        // This code will only run on the client, after the component has mounted.
        let initialLanguage = LanguageOption_Global;
        const saved = localStorage.getItem(localStoreName);

        if (saved) {
             try {
                initialLanguage = JSON.parse(saved);
            } catch (error) {
                console.error("Failed to parse language from localStorage", error);
            }
        } else {
            // If nothing is saved, try to determine language from timezone.
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (timezone) {
                // @ts-ignore
                const country = TimeZones[timezone]?.c[0];
                switch (country) {
                    case 'RO': case 'MD': initialLanguage = LanguageOption_RO; break;
                    case 'GB': initialLanguage = LanguageOption_UK; break;
                    case 'HK': initialLanguage = LanguageOption_HK; break;
                    case 'DE': initialLanguage = LanguageOption_DE; break;
                    case 'FR': initialLanguage = LanguageOption_FR; break;
                    case 'ES': initialLanguage = LanguageOption_ES; break;
                    case 'IT': initialLanguage = LanguageOption_IT; break;
                    case 'US': initialLanguage = LanguageOption_US; break;
                }
            }
        }

        isLoaded.value = true;
        // Set the language. This will trigger the watcher if the language is different.
        setLanguage(initialLanguage);
    });

    const saveToDisk = () => {
        localStorage.setItem(localStoreName, JSON.stringify(selected.value));
    }

    watch(selected, () => {
        if (isLoaded.value && selected.value) {
            saveToDisk();
        }
    }, {deep: true});

    const setLanguage = (language: LanguageOption) => {
        selected.value = language;
        $i18n.setLocale(language.code);
    }

    return {
        selected,
        setLanguage
    }
})